import React from 'react'
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  ListItemIcon,
  ListItemText,
  FormHelperText,
} from '@material-ui/core'
import useStyles from './styles'
import { useFeatureFlags, flags } from 'lib/feature-flags'
import { Controller } from 'react-hook-form'
import MUIRichTextEditor from 'mui-rte'
import { Link } from 'react-router-dom'
import { markdownToDraft, draftToMarkdown } from 'markdown-draft-js'
import { convertToRaw } from 'draft-js'
import StartTimeIncrementPreviewDialog from 'components/start-time-increment-preview-dialog'

const BookingTypeDetails = ({
  errors,
  control,
  register,
  coachFacilities,
  durationInMinutesOptions,
  categories,
  setValue,
  handleCategoryChange,
  startTimeIncrementOptions,
}) => {
  const classes = useStyles()

  const [isBookingStartTimeEnabled] = useFeatureFlags([
    flags.FLAG_FEAT_BOOKING_START_TIMES,
  ])

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Booking Details
      </Typography>
      <Box mt={3} mb={5}>
        <Typography variant="subtitle1" gutterBottom>
          Category
        </Typography>
        <Typography variant="body2" gutterBottom>
          Select from the categories below to indicate the type of booking.
        </Typography>
        <FormControl error={!!errors.category} className={classes.formField}>
          <InputLabel id="category">Select category</InputLabel>
          <Controller
            render={({ onChange, value }) => (
              <Select
                labelId="category"
                data-cy="category"
                label="Select category"
                onChange={e => {
                  onChange(e)
                  handleCategoryChange(e)
                }}
                value={value || ''}
                classes={{
                  root: classes.menuItem,
                }}
              >
                {categories.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    <ListItemIcon className={classes.icon}>
                      {opt.icon}
                    </ListItemIcon>
                    <ListItemText>{opt.label}</ListItemText>
                  </MenuItem>
                ))}
              </Select>
            )}
            name="category"
            control={control}
          />
        </FormControl>

        <Typography variant="subtitle1" gutterBottom>
          Name
        </Typography>
        <Typography variant="body2" gutterBottom>
          Enter a name for this booking type. Feel free to edit the suggested
          name based on the category you've selected.
        </Typography>
        <TextField
          className={classes.formField}
          name="title"
          data-cy="title"
          inputRef={register}
          error={!!errors.title}
          helperText={errors.title && 'this field is required'}
        />

        <Typography variant="subtitle1" gutterBottom>
          Location
        </Typography>
        <Typography variant="body2" gutterBottom>
          Select the location where this booking type will be associated.
        </Typography>
        <FormControl
          error={!!errors.location || !coachFacilities.length}
          variant="outlined"
          className={classes.formField}
        >
          <InputLabel id="category">Select location</InputLabel>
          <Controller
            as={
              <Select
                labelId="location"
                label="Select location"
                value=""
                data-cy="location"
              >
                {coachFacilities.map(opt => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.name}
                  </MenuItem>
                ))}
              </Select>
            }
            name="location"
            control={control}
          />
          {!coachFacilities.length ? (
            <FormHelperText>
              Please{' '}
              <Link to="/pga-coach/settings/availability">
                set your availability
              </Link>{' '}
              before creating a booking type
            </FormHelperText>
          ) : null}
        </FormControl>

        <Typography variant="subtitle1" gutterBottom>
          Duration
        </Typography>
        <Typography variant="body2" gutterBottom>
          Select a time duration to indicate how long each session under this
          booking type will last.
        </Typography>
        <FormControl
          error={!!errors.durationInMinutes}
          className={classes.formField}
        >
          <InputLabel id="durationInMinutes">Select time duration</InputLabel>
          <Controller
            as={
              <Select
                labelId="durationInMinutes"
                label="Select time duration"
                data-cy="duration"
              >
                {durationInMinutesOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            }
            name="durationInMinutes"
            control={control}
          />
        </FormControl>
        {isBookingStartTimeEnabled && (
          <>
            <Typography variant="subtitle1" gutterBottom>
              Start Time Increments
            </Typography>
            <Typography variant="body2" gutterBottom>
              Set the frequency of available start times displayed in the
              calendar for this booking type. The start times are based on your
              availability at the selected location.
            </Typography>
            <FormControl
              error={!!errors.startTimeIncrementInMinutes}
              className={classes.formField}
            >
              <InputLabel id="startTimeIncrementInMinutes">
                Select start time increment
              </InputLabel>
              <Controller
                as={
                  <Select
                    labelId="startTimeIncrementInMinutes"
                    label="Select start time interval"
                    data-cy="startTimeInterval"
                    defaultValue={
                      startTimeIncrementOptions.filter(o => o.default)[0].value
                    }
                  >
                    {startTimeIncrementOptions.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                }
                name="startTimeIncrementInMinutes"
                control={control}
              />
              <StartTimeIncrementPreviewDialog
                startTimeIncrementOptions={startTimeIncrementOptions}
              />
            </FormControl>
          </>
        )}
        <Typography variant="subtitle1" gutterBottom>
          Description
        </Typography>
        <Typography variant="body2" gutterBottom>
          Provide a description for this booking type. Include information that
          would be helpful for your students to know when they book a session.
        </Typography>
        <Box className={classes.editorRoot}>
          <Controller
            render={({ onChange, value }, _) => (
              <>
                <MUIRichTextEditor
                  label="Add a description"
                  controls={['title', 'bold', 'italic', 'bulletList']}
                  defaultValue={JSON.stringify(
                    markdownToDraft(
                      control.defaultValuesRef.current.description,
                    ),
                  )}
                  onChange={e => {
                    onChange(e)
                    const content = convertToRaw(e.getCurrentContent())
                    setValue('description', draftToMarkdown(content))
                  }}
                />
              </>
            )}
            name="description"
            control={control}
            defaultValue={null}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default BookingTypeDetails
